import { WindowSpec } from '@cutr/constants/windows/windows';
import {
  getAreas,
  getFrameDimensions,
  getInnerBeams,
} from '@cutr/constants/windows/windows-geometry.ts';
import React from 'react';
import { Layer, Stage } from 'react-konva';

import { Areas } from './Areas';
import { BottomCaptions, LeftCaptions } from './DimensionCaptions';
import { Frame } from './frames/Frame';
import { KonvaProvider, useKonva } from './KonvaContext';
import { getOffsets, RenderStyles } from './renderStyles';

type WindowThumbnailProps = {
  windowSpec: WindowSpec;
  containerHeight?: number;
  positionId?: string;
  renderStyles: RenderStyles;
};

export const WindowThumbnail = ({
  windowSpec,
  containerHeight = 140,
  positionId,
  renderStyles,
}: WindowThumbnailProps) => {
  const containerId = React.useId();

  return (
    <KonvaProvider
      containerId={containerId}
      key={`${containerId}-konvaProvider`}
      stageScaleX={0}
      stageScaleY={0}
      stageWidth={0}
      stageHeight={0}
      renderStyles={renderStyles}
      positionId={positionId}
    >
      <WindowThumbnailContent
        windowSpec={windowSpec}
        containerHeight={containerHeight}
      />
    </KonvaProvider>
  );
};

const WindowThumbnailContent = ({
  windowSpec,
  containerHeight = 140,
}: {
  windowSpec: WindowSpec;
  containerHeight: number;
}) => {
  const {
    stageRef,
    setStageDimensions,
    containerId,
    stageScaleX,
    stageScaleY,
    stageWidth,
    stageHeight,
    renderStyles,
  } = useKonva();

  const { topMM, bottomMM, leftMM } = getOffsets(renderStyles);
  const { frameWidthMM, frameHeightMM } = getFrameDimensions(
    windowSpec.frameShape
  );

  const ratio = containerHeight / frameHeightMM;
  const containerWidth = frameWidthMM * ratio;

  React.useLayoutEffect(() => {
    const container = document.querySelector<HTMLDivElement>(`#${containerId}`);
    if (!container) return;

    const parent = container.parentElement;
    if (!parent) return;

    const parentHeight = parent.clientHeight;
    const parentWidth = parent.clientWidth;

    const updateStageDimensions = (
      newParentWidth: number,
      newParentHeight: number
    ) => {
      const { topMM, bottomMM, leftMM, rightMM } = getOffsets(renderStyles);
      const { frameHeightMM } = getFrameDimensions(windowSpec.frameShape);
      const totalHeight = frameHeightMM + topMM + bottomMM;
      const totalWidth = frameWidthMM + leftMM + rightMM;

      const stageScaleY = newParentHeight / totalHeight;
      const stageScaleX = newParentWidth / totalWidth;

      const scale = Math.min(stageScaleX, stageScaleY);

      setStageDimensions({
        stageWidth: newParentWidth,
        stageHeight: newParentHeight,
        stageScaleX: scale,
        stageScaleY: scale,
      });
    };

    updateStageDimensions(parentWidth, parentHeight);
  }, [
    containerId,
    frameWidthMM,
    windowSpec.frameShape,
    setStageDimensions,
    renderStyles,
  ]);
  return (
    <div
      style={{
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
      }}
    >
      <Stage
        id={containerId}
        scaleY={-stageScaleY}
        scaleX={stageScaleX}
        width={stageWidth}
        height={stageHeight}
        offsetY={frameHeightMM + bottomMM + topMM}
        ref={stageRef}
      >
        <Layer listening={false}>
          <Frame
            frame={windowSpec.frameShape}
            x={leftMM}
            y={bottomMM}
            innerBeams={getInnerBeams(windowSpec)}
          />
          {renderStyles.dimensionGuidelines !== 'none' && stageHeight > 0 && (
            <>
              <LeftCaptions
                frameHeightMM={frameHeightMM}
                frameY={bottomMM}
                windowSpec={windowSpec}
              />
              <BottomCaptions
                frameWidthMM={frameWidthMM}
                frameX={leftMM}
                windowSpec={windowSpec}
              />
            </>
          )}
          <Areas
            windowSpec={windowSpec}
            areas={getAreas(windowSpec)}
            x={leftMM}
            y={bottomMM}
            areaConfigurations={windowSpec.areaConfigurations}
          />
        </Layer>
      </Stage>
    </div>
  );
};
